import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "module/api";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";

export type TrackingMode = "unique" | "same";

export interface VariableFileOption {
  id: string;
  name: string;
  columns: string[];
  rowsAmount: number;
  metadata?: Record<string, any>;
}

export interface TrackingConfigState {
  promoEnabled: boolean;
  qrEnabled: boolean;
  promoMode: TrackingMode | null;
  qrMode: TrackingMode | null;
  staticCouponCode: string;
  qrCodeLink: string;
  promoPlaceholder: string | null;
  qrPlaceholder: string | null;
  variableFileId: string | null;
  columnMapping: Record<string, string>;
}

const URL_PATTERN = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;
export const isValidUrl = (value: string) => URL_PATTERN.test(value.trim());

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const autoMatchColumn = (placeholder: string, columns: string[]): string | undefined => {
  const target = normalize(placeholder);
  return (
    columns.find((c) => normalize(c) === target) ??
    columns.find((c) => normalize(c).includes(target) || target.includes(normalize(c)))
  );
};

export interface UseTrackingConfigOptions {
  campaignId?: string;
  campaignData: {
    couponType?: string | null;
    staticCouponCode?: string | null;
    isTrackableCoupon?: number | null;
    qrCodeLink?: string | null;
  } | null;
  campaignVariables: any[];
  variableFiles: VariableFileOption[];
  placeholders: string[];
  refetchCampaignVariables: () => void;
}

export const useTrackingConfig = ({
  campaignId,
  campaignData,
  campaignVariables,
  variableFiles,
  placeholders,
  refetchCampaignVariables,
}: UseTrackingConfigOptions) => {
  const [state, setState] = useState<TrackingConfigState>({
    promoEnabled: false,
    qrEnabled: false,
    promoMode: null,
    qrMode: null,
    staticCouponCode: "",
    qrCodeLink: "",
    promoPlaceholder: null,
    qrPlaceholder: null,
    variableFileId: null,
    columnMapping: {},
  });

  const hydratedFor = useRef<string | null>(null);
  const lastPersistedMappingRef = useRef<string>("");
  useEffect(() => {
    if (!campaignData || !campaignId || hydratedFor.current === campaignId) return;
    hydratedFor.current = campaignId;
    const linkedVariable = campaignVariables?.[0]?.variable;
    const linkedId = linkedVariable?.id;
    const meta = linkedVariable?.metadata ?? {};
    lastPersistedMappingRef.current = JSON.stringify({
      m: meta.placeholderMapping ?? {},
      p: meta.promoPlaceholder ?? null,
      q: meta.qrPlaceholder ?? null,
    });
    const couponType = campaignData.couponType ?? null;
    const hasQr = !!campaignData.qrCodeLink;
    setState((prev) => ({
      ...prev,
      promoEnabled: couponType === "static" || couponType === "variable",
      promoMode: couponType === "variable" ? "unique" : couponType === "static" ? "same" : null,
      staticCouponCode: campaignData.staticCouponCode ?? "",
      qrEnabled: hasQr,
      qrMode: hasQr ? "same" : null,
      qrCodeLink: campaignData.qrCodeLink ?? "",
      variableFileId: linkedId != null ? String(linkedId) : null,
      columnMapping: meta.placeholderMapping ?? prev.columnMapping,
      promoPlaceholder: meta.promoPlaceholder ?? prev.promoPlaceholder,
      qrPlaceholder: meta.qrPlaceholder ?? prev.qrPlaceholder,
    }));
  }, [campaignData, campaignId, campaignVariables]);

  const update = useCallback((patch: Partial<TrackingConfigState>) => setState((prev) => ({ ...prev, ...patch })), []);

  const selectedFile = useMemo(
    () => variableFiles.find((f) => f.id === state.variableFileId) ?? null,
    [variableFiles, state.variableFileId]
  );

  const persistCoupon = useCallback(() => {
    if (!campaignId) return Promise.resolve();
    const isVariable = state.promoEnabled && state.promoMode === "unique";
    const isStatic = state.promoEnabled && state.promoMode === "same";
    return updateCampaignExtraData({
      campaignId,
      isTrackableCoupon: state.promoEnabled ? 1 : 0,
      couponType: isVariable ? "variable" : isStatic ? "static" : null,
      staticCouponCode: isStatic ? state.staticCouponCode || null : null,
    });
  }, [campaignId, state.promoEnabled, state.promoMode, state.staticCouponCode]);

  const persistQr = useCallback(() => {
    if (!campaignId) return Promise.resolve();
    const sameUrl = state.qrEnabled && state.qrMode === "same";
    return updateCampaignExtraData({ campaignId, qrCodeLink: sameUrl ? state.qrCodeLink || null : null });
  }, [campaignId, state.qrEnabled, state.qrMode, state.qrCodeLink]);

  const persistMapping = useCallback(() => {
    if (!state.variableFileId) return Promise.resolve();
    const existing = selectedFile?.metadata ?? {};
    return apiClient.updateVariable(Number(state.variableFileId), {
      metadata: {
        ...existing,
        placeholderMapping: state.columnMapping,
        promoPlaceholder: state.promoPlaceholder,
        qrPlaceholder: state.qrPlaceholder,
      },
    });
  }, [state.variableFileId, state.columnMapping, state.promoPlaceholder, state.qrPlaceholder, selectedFile]);

  useEffect(() => {
    if (!state.variableFileId) return;
    const key = JSON.stringify({
      m: state.columnMapping,
      p: state.promoPlaceholder,
      q: state.qrPlaceholder,
    });
    if (key === lastPersistedMappingRef.current) return;
    lastPersistedMappingRef.current = key;
    void persistMapping();
  }, [state.variableFileId, state.columnMapping, state.promoPlaceholder, state.qrPlaceholder, persistMapping]);

  const selectFile = useCallback(
    async (fileId: string | null, knownColumns?: string[]) => {
      const file = variableFiles.find((f) => f.id === fileId) ?? null;
      const columns = knownColumns ?? file?.columns ?? [];
      const previousId = state.variableFileId;
      const mapping: Record<string, string> = {};
      if (fileId)
        placeholders.forEach((p) => {
          const col = autoMatchColumn(p, columns);
          if (col) mapping[p] = col;
        });
      update({ variableFileId: fileId, columnMapping: mapping });
      if (!campaignId) return;
      try {
        if (previousId && previousId !== fileId)
          await apiClient.unlinkVariableWithCampaign(campaignId, Number(previousId));
        if (fileId) await apiClient.linkVariableWithCampaign(campaignId, Number(fileId));
        refetchCampaignVariables();
      } catch (err) {
        console.error("selectFile variable link error", err);
      }
    },
    [campaignId, variableFiles, placeholders, state.variableFileId, update, refetchCampaignVariables]
  );

  const mapColumn = useCallback((placeholder: string, column: string) => {
    setState((prev) => {
      const next = { ...prev.columnMapping };
      Object.keys(next).forEach((key) => {
        if (next[key] === column) delete next[key];
      });
      next[placeholder] = column;
      return { ...prev, columnMapping: next };
    });
  }, []);

  const needsVariableFile =
    (state.promoEnabled && state.promoMode === "unique") || (state.qrEnabled && state.qrMode === "unique");

  // The variable file is OPTIONAL for submit (matches legacy — `variableId` is
  // never required there). But once a file IS selected, every detected placeholder
  // must be mapped to one of its columns.
  const hasUnmappedPlaceholders = useMemo(
    () => !!state.variableFileId && placeholders.some((p) => !state.columnMapping[p]),
    [state.variableFileId, state.columnMapping, placeholders]
  );

  const isValid = useMemo(() => {
    if (!state.promoEnabled && !state.qrEnabled) return true;
    if (state.promoEnabled) {
      if (!state.promoMode) return false;
      if (state.promoMode === "same" && !state.staticCouponCode.trim()) return false;
      if (state.promoMode === "unique" && !state.promoPlaceholder) return false;
    }
    if (state.qrEnabled) {
      if (!state.qrMode) return false;
      if (state.qrMode === "same" && !isValidUrl(state.qrCodeLink)) return false;
      if (state.qrMode === "unique" && !state.qrPlaceholder) return false;
    }
    // The variable file itself is OPTIONAL; only its mapping is required once chosen.
    if (hasUnmappedPlaceholders) return false;
    return true;
  }, [state, hasUnmappedPlaceholders]);

  return {
    state,
    update,
    selectedFile,
    selectFile,
    mapColumn,
    persistCoupon,
    persistQr,
    needsVariableFile,
    hasUnmappedPlaceholders,
    isValid,
  };
};
